/**
 * UnsavedChangesDialog
 *
 * FT-449 — In-app "You have unsaved changes" confirmation dialog.
 *
 * Used by AddCrewView (via useUnsavedChangesGuard's dialogProps).
 * Exported separately so it can be reused by any other guard in the future.
 *
 * Props:
 *   isOpen  {boolean}   — whether the dialog is visible
 *   onStay  {function}  — user clicked "Stay" (keep them on the form)
 *   onLeave {function}  — user clicked "Leave without saving" (discard + go)
 *
 * Design:
 *   - Tailwind-styled, matches the existing ConfirmationModal pattern
 *   - Accessible: role="alertdialog", aria-labelledby, aria-describedby
 *   - 44px minimum touch targets on both buttons (mobile-safe at 390×844)
 *   - "Stay" is autofocused so keyboard / screen-reader users land on the safe action
 *   - z-[9999] so it sits above every other layer (including ConfirmationModal z-50)
 *   - Backdrop click invokes onStay (conservative: accidental tap keeps user on form)
 */

import React from 'react';

export const UnsavedChangesDialog = ({ isOpen, onStay, onLeave }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto" aria-modal="true" role="dialog">
      {/* Backdrop — clicking it invokes Stay (conservative default) */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 transition-opacity"
        onClick={onStay}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-sm w-full p-6"
          role="alertdialog"
          aria-labelledby="unsaved-dialog-title"
          aria-describedby="unsaved-dialog-body"
        >
          {/* Icon + heading */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-yellow-50">
              <svg
                className="h-5 w-5 text-yellow-600"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            <div className="flex-1">
              <h3
                id="unsaved-dialog-title"
                className="text-base font-semibold text-gray-900 mb-1"
              >
                You have unsaved changes
              </h3>
              <p
                id="unsaved-dialog-body"
                className="text-sm text-gray-500"
              >
                Leave without saving? Your changes will be lost.
              </p>
            </div>
          </div>

          {/* Buttons
              flex-col-reverse + sm:flex-row: on mobile, Stay renders below Leave
              (closer to thumb); at sm+, Stay is on the right (primary position).  */}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            {/* Leave — secondary */}
            <button
              type="button"
              onClick={onLeave}
              className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 min-h-[44px]"
              data-testid="unsaved-leave-btn"
            >
              Leave without saving
            </button>

            {/* Stay — primary, autofocused */}
            <button
              type="button"
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
              onClick={onStay}
              className="w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 min-h-[44px]"
              data-testid="unsaved-stay-btn"
            >
              Stay
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnsavedChangesDialog;
